import { Link } from '@tanstack/react-router';
import { Check, X } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { RoleTemplateView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Chip } from '../../../../components/Chip';
import { Field } from '../../../../components/Field';
import { SearchInput, Textarea } from '../../../../components/Input';
import { Popover, usePopover } from '../../../../components/Popover';
import { Select } from '../../../../components/Select';
import { errorMessage } from '../../../../lib/api-client';
import { useFocusOnOpen } from '../../../../lib/use-focus-on-open';
import { useToasts } from '../../../../stores/toasts';
import { useDecideRoleTemplate } from '../../api/jobs-api';
import { groupedTemplates, matchesTemplate, templatePlaceOf, usedText } from './role-template-draft';
import styles from './AdminSetup.module.css';

// Role templates A: the library on the left — "Waiting for approval" on top (approve in one click, reject with a
// reason in a box beside the button), then every template with its chips, the rejection reason under its name, and how
// many jobs were made from it. "Find a template" searches both. At laptop width and below, a drop-down instead.

const STEPS: Readonly<Record<string, 1 | -1>> = { ArrowDown: 1, ArrowUp: -1 };
const REJECT_WIDTH = 320;

interface TemplateLibraryProps {
  readonly templates: readonly RoleTemplateView[];
  readonly selectedId: string | null;
  readonly canEdit: boolean;
  readonly onSelect: (id: string) => void;
}

export function TemplateLibrary({ templates, selectedId, canEdit, onSelect }: TemplateLibraryProps): ReactNode {
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const { waiting, library } = groupedTemplates(templates.filter((template) => matchesTemplate(template, query)));
  const shown = [...waiting, ...library];

  // 06-qol §2: the arrow keys move through both groups, in the order they are drawn.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = STEPS[event.key];
    if (step === undefined || shown.length === 0 || !(event.target instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    const index = shown.findIndex((template) => template.id === selectedId);
    const next = shown[Math.min(shown.length - 1, Math.max(0, index + step))];
    if (!next || next.id === selectedId) return;
    listRef.current?.querySelectorAll<HTMLAnchorElement>('a[data-template]')[shown.indexOf(next)]?.focus();
    onSelect(next.id);
  };

  const link = (template: RoleTemplateView, children: ReactNode): ReactNode => {
    const isSelected = template.id === selectedId;
    return (
      <Link
        to="/admin/role-templates/$templateId"
        params={{ templateId: template.id }}
        className={styles.item}
        data-template
        aria-current={isSelected ? 'page' : undefined}
        tabIndex={isSelected || (selectedId === null && template === shown[0]) ? 0 : -1}
      >
        {children}
      </Link>
    );
  };

  return (
    <nav className={styles.pane} aria-label="Role templates">
      <SearchInput aria-label="Find a template" placeholder="Find a template" value={query} onChange={(event) => setQuery(event.target.value)} />
      {shown.length === 0 && query.trim() !== '' && (
        <div className={styles.empty}>
          <span>No template matches “{query.trim()}”.</span>
          <Button variant="ghost" size="sm" onClick={() => setQuery('')}>
            Clear
          </Button>
        </div>
      )}
      <div ref={listRef} className={styles.list} onKeyDown={onKeyDown}>
        {waiting.length > 0 && (
          <section className={styles.queue} aria-label="Waiting for approval">
            <h3 className={styles.groupHead}>
              Waiting for approval <span className={styles.count}>{waiting.length}</span>
            </h3>
            <ul className={styles.list}>
              {waiting.map((template) => (
                <li key={template.id}>
                  {link(
                    template,
                    <>
                      <span className={styles.itemName}>{template.name}</span>
                      <span className={styles.itemMeta}>
                        {[template.submittedByName ? `From ${template.submittedByName}` : 'Submitted', template.ventureName, template.departmentName].filter(Boolean).join(' · ')}
                      </span>
                    </>,
                  )}
                  {canEdit && <Decision template={template} />}
                </li>
              ))}
            </ul>
          </section>
        )}
        {library.length > 0 && (
          <section aria-label="Library">
            <h3 className={styles.groupHead}>
              Library <span className={styles.count}>{library.length}</span>
            </h3>
            <ul className={styles.list}>
              {library.map((template) => (
                <li key={template.id}>
                  {link(
                    template,
                    <>
                      <span className={styles.itemTop}>
                        <span className={styles.itemName}>{template.name}</span>
                        {template.isCertified && <Chip tone="success">Certified</Chip>}
                        {!template.isActive && (
                          <Chip tone="ended" plain>
                            Off
                          </Chip>
                        )}
                        {template.status === 'rejected' && <Chip tone="danger">Rejected</Chip>}
                      </span>
                      <span className={styles.itemMeta}>
                        {templatePlaceOf(template)} · {usedText(template.timesUsed)}
                      </span>
                      {template.status === 'rejected' && template.decisionReason && <span className={styles.itemReason}>{template.decisionReason}</span>}
                    </>,
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </nav>
  );
}

/**
 * F19.05: Approve in one click; Reject asks for the reason beside the button (#2), never centred. The reason typed stays
 * in the box if the rejection is refused.
 */
export function Decision({ template }: { readonly template: RoleTemplateView }): ReactNode {
  const decide = useDecideRoleTemplate();
  const show = useToasts((state) => state.show);
  const { anchor, triggerRef, isOpen, toggle, close } = usePopover();
  const [reason, setReason] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);
  useFocusOnOpen(reasonRef, isOpen);
  const isRejectable = reason.trim() !== '';

  // The decided template leaves the queue at once (optimistic), unmounting this; a promise still answers, a per-call
  // callback would not.
  const approve = async (): Promise<void> => {
    try {
      await decide.mutateAsync({ id: template.id, version: template.version, decision: 'approve' });
      show({ tone: 'success', message: `${template.name} approved`, detail: 'Recruiters can pick it from now on.' });
    } catch (cause) {
      show({ tone: 'danger', message: 'The template was not approved', detail: errorMessage(cause) });
    }
  };

  const reject = async (): Promise<void> => {
    if (!isRejectable) return;
    const said = reason.trim();
    close();
    try {
      await decide.mutateAsync({ id: template.id, version: template.version, decision: 'reject', reason: said });
      show({ tone: 'success', message: `${template.name} rejected`, detail: 'The reason is kept with the template.' });
    } catch (cause) {
      show({ tone: 'danger', message: 'The template was not rejected', detail: errorMessage(cause) });
    }
  };

  return (
    <div className={styles.itemActions}>
      <Button size="sm" variant="solid" icon={Check} disabled={decide.isPending} onClick={() => void approve()}>
        Approve
      </Button>
      <Button ref={triggerRef} size="sm" variant="ghost" icon={X} aria-haspopup="dialog" aria-expanded={isOpen} disabled={decide.isPending} onClick={toggle}>
        Reject…
      </Button>
      <Popover anchor={anchor} onClose={close} width={REJECT_WIDTH} label={`Reject ${template.name}`}>
        <form
          className={styles.ask}
          onSubmit={(event) => {
            event.preventDefault();
            void reject();
          }}
        >
          <h3 className={styles.askTitle}>Reject {template.name}?</h3>
          <p className={styles.askBody}>Say why, so {template.submittedByName ?? 'the department'} can fix it. Nobody can use it once it is rejected.</p>
          <Field label="Reason" isRequired>
            {({ inputId }) => (
              <Textarea
                ref={reasonRef}
                id={inputId}
                rows={3}
                maxLength={1000}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                    event.preventDefault();
                    void reject();
                  }
                }}
              />
            )}
          </Field>
          <div className={styles.askActions}>
            <Button type="button" variant="ghost" size="sm" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="dangerSolid" size="sm" disabled={!isRejectable}>
              Reject template
            </Button>
          </div>
        </form>
      </Popover>
    </div>
  );
}

/** Laptop, tablet and phone: the template is picked from a drop-down; one waiting is marked so. */
export function TemplatePicker({ templates, selectedId, onSelect }: Omit<TemplateLibraryProps, 'canEdit'>): ReactNode {
  const { waiting, library } = groupedTemplates(templates);
  return (
    <div className={styles.picker}>
      <Select
        label="Role template"
        placeholder="New template"
        value={selectedId}
        onChange={onSelect}
        options={[...waiting, ...library].map((template) => ({
          value: template.id,
          label: template.status === 'draft' ? `${template.name} · waiting for approval` : template.name,
          description: `${templatePlaceOf(template)} · ${usedText(template.timesUsed)}`,
          searchText: `${template.name} ${template.title} ${template.roleFamily ?? ''}`,
        }))}
      />
    </div>
  );
}
